import { SyncCoreDataDefinitionsEnumTranslator, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';

/**
 * A term's curation status: what it reads as, and what an operator may do to it next.
 *
 * Shared rather than decided per screen. The term list and a term's own page both offer status
 * actions, and two lists that drifted apart would let an operator approve a suggestion from one
 * screen and not the other — or, worse, offer a transition the server refuses.
 */

export function isActiveTerm(status?: TaxonomyTermStatus) {
  return status === TaxonomyTermStatus.Active;
}

export function isUnapprovedTerm(status?: TaxonomyTermStatus) {
  return status === TaxonomyTermStatus.Unapproved;
}

export function statusLabel(status: TaxonomyTermStatus): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermStatus', status);
}

/** One status change offered on a term, named as the operator's action rather than as a state. */
export interface ITaxonomyTermStatusAction {
  label: string;
  nextStatus: TaxonomyTermStatus;
}

export function availableStatusActions(status?: TaxonomyTermStatus): ITaxonomyTermStatusAction[] {
  switch (status) {
    case TaxonomyTermStatus.Unapproved:
      return [
        { label: 'Approve', nextStatus: TaxonomyTermStatus.Active },
        { label: 'Dismiss', nextStatus: TaxonomyTermStatus.Archived },
      ];
    case TaxonomyTermStatus.Active:
      return [{ label: 'Archive', nextStatus: TaxonomyTermStatus.Archived }];
    case TaxonomyTermStatus.Archived:
      return [{ label: 'Reactivate', nextStatus: TaxonomyTermStatus.Active }];
    default:
      return [];
  }
}

/**
 * The status move that puts a tag into use, where its status offers one.
 *
 * Approving a suggestion and reactivating an archived tag are what an operator opens a tag to do,
 * so a screen with room for a button spends it on that one rather than leaving it behind a menu.
 * Read off the legal transitions instead of listed a second time: the move that makes a tag active
 * is the one worth promoting, and a status added to the transitions carries its own answer with it.
 */
export function primaryStatusAction(status?: TaxonomyTermStatus): ITaxonomyTermStatusAction | undefined {
  return availableStatusActions(status).find((action) => action.nextStatus === TaxonomyTermStatus.Active);
}

/**
 * The moves left for a menu once a screen promoted one: every legal transition but that one.
 *
 * Told apart by the status they lead to, which is what a transition IS here — every call to the
 * transitions builds its own objects, so comparing the values themselves would drop nothing.
 */
export function secondaryStatusActions(status?: TaxonomyTermStatus): ITaxonomyTermStatusAction[] {
  const promoted = primaryStatusAction(status);
  return availableStatusActions(status).filter((action) => action.nextStatus !== promoted?.nextStatus);
}

/** Whether a term in `status` may be moved to `nextStatus` at all. */
export function allowsStatusTransition(status: TaxonomyTermStatus | undefined, nextStatus: TaxonomyTermStatus): boolean {
  return availableStatusActions(status).some((action) => action.nextStatus === nextStatus);
}

/** Classes for a term card and for its status badge, distinguishing suggestions awaiting review. */
export function taxonomyTermStatusStyles(status?: TaxonomyTermStatus): { card: string; badge: string } {
  if (isUnapprovedTerm(status)) {
    // Suggestions (untracked co-subjects, aspect phrases) awaiting an approve/dismiss decision.
    return {
      card: 'border-amber-200 bg-amber-50/70 text-slate-900 hover:border-amber-300 hover:shadow-card-hover',
      badge: 'bg-amber-50 border-amber-200 text-amber-700',
    };
  }

  if (isActiveTerm(status)) {
    return {
      card: 'border-slate-200/80 bg-white text-slate-900 shadow-sm hover:border-slate-300 hover:shadow-card-hover',
      badge: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    };
  }

  return {
    card: 'border-slate-200 bg-slate-50 text-slate-400',
    badge: 'bg-slate-100 border-slate-200 text-slate-500',
  };
}
